import { colors } from '../../styles/theme';
import { formatDate, formatMoney } from '../../lib/datetime';
import type { NavValuation, ProductBalance, WithdrawalPenaltyPreview } from '../../types/finance';

/**
 * What a partner reads before asking to leave a series priced at NAV (fixed-income plan v2, FI-14; owner decisions S-44,
 * S-45, S-47): the request is paid at the series' next exit window, at THAT window's approved NAV — not at today's value;
 * the figure shown is an estimate at the last approved NAV and never a promised amount; if many partners leave in the same
 * window, part of a request may be paid at the next one; closing the whole position also settles the management fee of the
 * days not yet charged. The early-exit charge, when there is one, is quoted and accepted in the form itself.
 */
export default function NavWindowNotice({
  windowAt,
  valuation,
  currencyCode,
  closing,
}: Readonly<{
  windowAt: string | null;
  valuation: NavValuation | null;
  currencyCode: string;
  closing: boolean;
}>): JSX.Element {
  return (
    <div
      className="rounded-lg p-3 text-sm space-y-1.5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.ink }}
    >
      <p className="m-0 font-semibold">This series pays at its exit windows.</p>
      <p className="m-0">
        Your request is paid at the approved NAV of that window, not at today&apos;s value.
      </p>
      {windowAt ? (
        <p className="m-0">{`Next exit window: ${formatDate(windowAt)}`}</p>
      ) : (
        <p className="m-0">The date of the next exit window is confirmed when you send your request.</p>
      )}
      {valuation && (
        <p className="m-0" style={{ color: colors.inkSoft }}>
          {`Estimate at the last approved NAV (${formatDate(valuation.as_of)}): ${formatMoney(valuation.value, currencyCode)} for your whole holding. It is an estimate, not a promised amount.`}
        </p>
      )}
      <p className="m-0" style={{ color: colors.inkSoft }}>
        If many partners ask to leave in the same window, part of your request may be paid at the next window.
      </p>
      {closing && (
        <p className="m-0" style={{ color: colors.inkSoft }}>
          Closing your whole holding also settles the management fee for the days not yet charged.
        </p>
      )}
    </div>
  );
}

/** The part of the withdraw form a series priced at NAV adds: closing the whole holding in one step, and the notice above.
 *  Nothing for any other product. */
export function NavExitSection({
  selected,
  preview,
  closing,
  onCloseAll,
}: Readonly<{
  selected: ProductBalance | undefined;
  preview: WithdrawalPenaltyPreview | null;
  closing: boolean;
  onCloseAll: (amount: string) => void;
}>): JSX.Element | null {
  const windowAt = preview?.exit_window_at ?? null;
  if (!selected || (!selected.nav && !windowAt)) return null;
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => onCloseAll(selected.balance)}
        className="text-sm underline"
        style={{ background: 'none', border: 'none', padding: 0, color: colors.ink, cursor: 'pointer' }}
      >
        Withdraw everything and close my holding
      </button>
      <NavWindowNotice
        windowAt={windowAt}
        valuation={selected.nav ?? null}
        currencyCode={selected.currency_code}
        closing={closing}
      />
    </div>
  );
}
